import React from 'react';
import { createPortal } from 'react-dom';
import { Bell, Brain } from 'lucide-react';
import { AssistantWidthMenu, type AssistantWidthMenuMode } from './AssistantWidthMenu';
import styles from '../styles/CrossHeaderActions.module.css';

export const crossHeaderMiddleSlotTestIds = {
  schoolNightModeToggle: { testId: 'school-shell-header-night-mode-toggle' },
  parentNightModeToggle: { testId: 'parent-shell-header-night-mode-toggle' },
};

const ASSISTANT_WIDTH_MENU_WIDTH_PX = 156;
const ASSISTANT_WIDTH_MENU_VIEWPORT_INSET_PX = 8;
const ASSISTANT_WIDTH_MENU_ANCHOR_GAP_PX = 8;
const ASSISTANT_WIDTH_MENU_FALLBACK_HEIGHT_PX = 184;
const ASSISTANT_WIDTH_MENU_POPOVER_Z_INDEX = 1100;

type AssistantMenuPosition = {
  top: number;
  left: number;
};

type AssistantMenuSurfaceContext = {
  shellTheme?: string;
  shellRenderPolicy?: string;
};

const clamp = (value: number, min: number, max: number): number => (
  Math.min(Math.max(value, min), Math.max(min, max))
);

const readAssistantMenuSurfaceContext = (
  anchor: HTMLElement | null,
): AssistantMenuSurfaceContext => {
  const themedSurface = anchor?.closest<HTMLElement>('[data-shared-shell-theme]');
  return {
    shellTheme: themedSurface?.getAttribute('data-shared-shell-theme') || undefined,
    shellRenderPolicy: themedSurface?.getAttribute('data-shell-render-policy') || undefined,
  };
};

export type CrossHeaderActionsProps = {
  testIdPrefix?: string;
  assistantExpanded: boolean;
  notificationsOpen: boolean;
  unreadCount?: number;
  onAssistantToggle: () => void;
  onNotificationsToggle: () => void;
  assistantToggleDisabled?: boolean;
  hideAssistantToggleWhenExpandedOnDesktop?: boolean;
  assistantWidthMode?: AssistantWidthMenuMode;
  onAssistantWidthModeChange?: (mode: AssistantWidthMenuMode) => void;
  onAssistantClose?: () => void;
  className?: string;
  middleSlot?: React.ReactNode;
};

export const CrossHeaderActions: React.FC<CrossHeaderActionsProps> = ({
  testIdPrefix = 'cross-header',
  assistantExpanded,
  notificationsOpen,
  unreadCount = 0,
  onAssistantToggle,
  onNotificationsToggle,
  assistantToggleDisabled = false,
  hideAssistantToggleWhenExpandedOnDesktop = false,
  assistantWidthMode,
  onAssistantWidthModeChange,
  onAssistantClose,
  className,
  middleSlot,
}) => {
  const showAssistantMenu = Boolean(assistantWidthMode && onAssistantWidthModeChange && !assistantToggleDisabled);
  const assistantMenuAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const assistantMenuPortalRef = React.useRef<HTMLDivElement | null>(null);
  const assistantMenuCloseTimerRef = React.useRef<number | null>(null);
  const [assistantMenuOpen, setAssistantMenuOpen] = React.useState(false);
  const [assistantMenuPosition, setAssistantMenuPosition] = React.useState<AssistantMenuPosition>({
    top: ASSISTANT_WIDTH_MENU_VIEWPORT_INSET_PX,
    left: ASSISTANT_WIDTH_MENU_VIEWPORT_INSET_PX,
  });
  const [assistantMenuSurfaceContext, setAssistantMenuSurfaceContext] =
    React.useState<AssistantMenuSurfaceContext>({});

  const updateAssistantMenuPosition = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const anchor = assistantMenuAnchorRef.current;
    if (!anchor) {
      return;
    }

    const nextSurfaceContext = readAssistantMenuSurfaceContext(anchor);
    setAssistantMenuSurfaceContext((current) => (
      current.shellTheme === nextSurfaceContext.shellTheme
      && current.shellRenderPolicy === nextSurfaceContext.shellRenderPolicy
        ? current
        : nextSurfaceContext
    ));

    const anchorRect = anchor.getBoundingClientRect();
    const menuRect = assistantMenuPortalRef.current?.getBoundingClientRect();
    const menuHeight = menuRect && menuRect.height > 0
      ? menuRect.height
      : ASSISTANT_WIDTH_MENU_FALLBACK_HEIGHT_PX;
    const viewportMaxLeft = window.innerWidth - ASSISTANT_WIDTH_MENU_WIDTH_PX - ASSISTANT_WIDTH_MENU_VIEWPORT_INSET_PX;
    const left = clamp(
      anchorRect.right - ASSISTANT_WIDTH_MENU_WIDTH_PX,
      ASSISTANT_WIDTH_MENU_VIEWPORT_INSET_PX,
      viewportMaxLeft,
    );
    const bottomTop = anchorRect.bottom + ASSISTANT_WIDTH_MENU_ANCHOR_GAP_PX;
    const top = bottomTop + menuHeight <= window.innerHeight - ASSISTANT_WIDTH_MENU_VIEWPORT_INSET_PX
      ? bottomTop
      : Math.max(
          ASSISTANT_WIDTH_MENU_VIEWPORT_INSET_PX,
          anchorRect.top - ASSISTANT_WIDTH_MENU_ANCHOR_GAP_PX - menuHeight,
        );

    setAssistantMenuPosition({
      top: Math.round(top),
      left: Math.round(left),
    });
  }, []);

  const clearAssistantMenuCloseTimer = () => {
    if (assistantMenuCloseTimerRef.current === null || typeof window === 'undefined') {
      return;
    }
    window.clearTimeout(assistantMenuCloseTimerRef.current);
    assistantMenuCloseTimerRef.current = null;
  };

  const openAssistantMenu = () => {
    clearAssistantMenuCloseTimer();
    if (showAssistantMenu) {
      updateAssistantMenuPosition();
      setAssistantMenuOpen(true);
    }
  };

  const closeAssistantMenu = () => {
    clearAssistantMenuCloseTimer();
    setAssistantMenuOpen(false);
  };

  const scheduleAssistantMenuClose = () => {
    clearAssistantMenuCloseTimer();
    if (typeof window === 'undefined') {
      setAssistantMenuOpen(false);
      return;
    }
    assistantMenuCloseTimerRef.current = window.setTimeout(() => {
      setAssistantMenuOpen(false);
      assistantMenuCloseTimerRef.current = null;
    }, 140);
  };

  React.useEffect(() => () => {
    clearAssistantMenuCloseTimer();
  }, []);

  React.useEffect(() => {
    if (!showAssistantMenu) {
      setAssistantMenuOpen(false);
      clearAssistantMenuCloseTimer();
    }
  }, [showAssistantMenu]);

  React.useLayoutEffect(() => {
    if (!showAssistantMenu || !assistantMenuOpen || typeof window === 'undefined') {
      return undefined;
    }

    updateAssistantMenuPosition();
    const frame = window.requestAnimationFrame(updateAssistantMenuPosition);
    window.addEventListener('resize', updateAssistantMenuPosition);
    window.addEventListener('scroll', updateAssistantMenuPosition, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateAssistantMenuPosition);
      window.removeEventListener('scroll', updateAssistantMenuPosition, true);
    };
  }, [assistantMenuOpen, showAssistantMenu, updateAssistantMenuPosition]);

  const isWithinAssistantMenuSurface = (node: Node | null) => Boolean(
    node
      && (
        assistantMenuAnchorRef.current?.contains(node)
        || assistantMenuPortalRef.current?.contains(node)
      ),
  );

  const handleWidthModeSelect = (nextMode: AssistantWidthMenuMode) => {
    if (assistantToggleDisabled || !onAssistantWidthModeChange) {
      return;
    }
    if (!assistantExpanded) {
      onAssistantToggle();
    }
    onAssistantWidthModeChange(nextMode);
    closeAssistantMenu();
  };

  const handleAssistantWidthMenuClose = () => {
    if (onAssistantClose) {
      onAssistantClose();
    } else if (assistantExpanded) {
      onAssistantToggle();
    }
    closeAssistantMenu();
  };

  const assistantWidthMenuNode = showAssistantMenu ? (
    <AssistantWidthMenu
      currentMode={assistantWidthMode ?? 'compact'}
      activeMode={assistantExpanded ? assistantWidthMode ?? 'compact' : 'closed'}
      onModeSelect={handleWidthModeSelect}
      onClose={handleAssistantWidthMenuClose}
      menuTestId={`${testIdPrefix}-assistant-width-menu`}
      optionTestIdPrefix={`${testIdPrefix}-assistant-width-option`}
      closeTestId={`${testIdPrefix}-assistant-width-close`}
      open={assistantMenuOpen}
      floating
    />
  ) : null;

  const assistantWidthMenuPortalNode = showAssistantMenu && assistantWidthMenuNode && typeof document !== 'undefined'
    ? createPortal(
        <div
          ref={assistantMenuPortalRef}
          data-testid={`${testIdPrefix}-assistant-width-menu-layer`}
          data-shell-overlay-kind="assistant-width-menu"
          data-shell-overlay-state={assistantMenuOpen ? 'open' : 'closed'}
          data-shared-shell-theme={assistantMenuSurfaceContext.shellTheme}
          data-shell-render-policy={assistantMenuSurfaceContext.shellRenderPolicy}
          style={{
            position: 'fixed',
            top: assistantMenuPosition.top,
            left: assistantMenuPosition.left,
            width: ASSISTANT_WIDTH_MENU_WIDTH_PX,
            zIndex: ASSISTANT_WIDTH_MENU_POPOVER_Z_INDEX,
            pointerEvents: assistantMenuOpen ? 'auto' : 'none',
          }}
          onMouseEnter={openAssistantMenu}
          onMouseLeave={scheduleAssistantMenuClose}
          onFocusCapture={openAssistantMenu}
          onBlurCapture={(event) => {
            const nextFocusedNode = event.relatedTarget as Node | null;
            if (isWithinAssistantMenuSurface(nextFocusedNode)) {
              return;
            }
            scheduleAssistantMenuClose();
          }}
          onKeyDownCapture={(event) => {
            if (event.key === 'Escape') {
              closeAssistantMenu();
            }
          }}
        >
          {assistantWidthMenuNode}
        </div>,
        document.body,
      )
    : assistantWidthMenuNode;

  return (
    <div
      className={[styles.actionGroup, className ?? ''].filter(Boolean).join(' ')}
      data-testid={`${testIdPrefix}-actions`}
    >
      <button
        className={`${styles.actionButton} ${notificationsOpen ? styles.actionButtonActive : ''}`}
        type="button"
        onClick={onNotificationsToggle}
        data-testid={`${testIdPrefix}-notifications-toggle`}
        aria-expanded={notificationsOpen}
        aria-label="Toggle notifications"
        title="Notifications"
      >
        <Bell className={styles.icon} />
        {unreadCount > 0 ? (
          <span
            className={styles.notificationBadge}
            data-testid={`${testIdPrefix}-notifications-badge`}
            aria-label={`${unreadCount} unread notifications`}
            title={`${unreadCount} unread notifications`}
          />
        ) : null}
      </button>
      {middleSlot}
      <div
        ref={assistantMenuAnchorRef}
        className={styles.assistantMenuAnchor}
        onMouseEnter={openAssistantMenu}
        onMouseLeave={scheduleAssistantMenuClose}
        onFocusCapture={openAssistantMenu}
        onBlurCapture={(event) => {
          const nextFocusedNode = event.relatedTarget as Node | null;
          if (isWithinAssistantMenuSurface(nextFocusedNode)) {
            return;
          }
          scheduleAssistantMenuClose();
        }}
        onKeyDownCapture={(event) => {
          if (event.key === 'Escape') {
            closeAssistantMenu();
          }
        }}
      >
        <button
          className={[
            styles.actionButton,
            assistantExpanded ? styles.actionButtonActive : '',
            hideAssistantToggleWhenExpandedOnDesktop && assistantExpanded
              ? styles.assistantButtonDesktopHidden
              : '',
          ].filter(Boolean).join(' ')}
          type="button"
          onClick={() => {
            closeAssistantMenu();
            onAssistantToggle();
          }}
          data-testid={`${testIdPrefix}-brain-toggle`}
          aria-pressed={assistantExpanded}
          aria-label={assistantToggleDisabled ? 'Assistant pinned open on wide school views' : 'Toggle assistant'}
          title={assistantToggleDisabled ? 'Assistant stays open on wide school views' : 'Toggle Intellio Assistant'}
          aria-haspopup={showAssistantMenu ? 'menu' : undefined}
          aria-expanded={showAssistantMenu ? assistantMenuOpen : assistantExpanded}
          disabled={assistantToggleDisabled}
        >
          <Brain className={styles.icon} />
        </button>
        {assistantWidthMenuPortalNode}
      </div>
    </div>
  );
};
